export type ImageFormat = 'svg' | 'png' | 'jpg' | 'webp' | 'gif';

/**
 * Formats that hold a logo's pixels exactly. A pick in one of these stays clean through every re-derive;
 * a JPEG or WebP keeps the compression noise it arrived with.
 */
export const LOSSLESS_FORMATS: ReadonlySet<ImageFormat> = new Set(['svg', 'png']);

/** Best first: a vector, then the raster that holds the logo exactly, then the lossy ones. */
export const FORMAT_PREFERENCE: readonly ImageFormat[] = ['svg', 'png', 'webp', 'jpg', 'gif'];

/**
 * The format a url's file extension names — the path's, never the query's. Storage download urls carry
 * the object path percent-encoded, so it is decoded first.
 */
export function imageFormatOf(url: string): ImageFormat | undefined {
    try {
        const path = decodeURIComponent(new URL(url).pathname);
        const ext = path.match(/\.([a-z0-9]+)$/i)?.[1];
        return ext ? imageFormatNamed(ext) : undefined;
    } catch {
        return undefined;
    }
}

const BY_NAME = new Map<string, ImageFormat>([
    ['svg', 'svg'],
    ['png', 'png'],
    ['jpg', 'jpg'],
    ['jpeg', 'jpg'],
    ['webp', 'webp'],
    ['gif', 'gif'],
]);

/** A format name as Brandfetch spells it (`jpeg`, `svg`, …), or undefined when it isn't an image format. */
export function imageFormatNamed(name: string): ImageFormat | undefined {
    return BY_NAME.get(name.toLowerCase());
}
